/**
 * Puts the virtual screen on the window (docs/spec/04 §4): WebGL2 with the CRT pass, or a plain 2D
 * copy when WebGL2 is not there. Also maps pointer positions back to virtual pixels.
 */

import { FRAGMENT, VERTEX } from './shaders';

export type CrtMode = 'off' | 'soft' | 'arcade';
export type ScaleMode = 'pixel' | 'fit';

interface CrtParams {
  scan: number;
  mask: number;
  bloom: number;
  curve: number;
  vignette: number;
}

const CRT: Record<CrtMode, CrtParams> = {
  off: { scan: 0, mask: 0, bloom: 0, curve: 0, vignette: 0 },
  soft: { scan: 0.28, mask: 0.06, bloom: 0.3, curve: 0, vignette: 0.3 },
  arcade: { scan: 0.55, mask: 0.22, bloom: 0.45, curve: 1, vignette: 0.55 },
};

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where a `vw` x `vh` image lands in a `W` x `H` window. `top`: against the top edge rather than
 * centred, leaving the room below for thumbs (touch controls on a tall phone).
 */
export function fitRect(vw: number, vh: number, W: number, H: number, mode: ScaleMode, top = false): Rect {
  let s = Math.min(W / vw, H / vh);
  // Whole pixels, unless that would waste more than a quarter of the window (a small phone).
  if (mode === 'pixel' && s >= 1 && Math.floor(s) / s >= 0.75) s = Math.floor(s);
  const w = Math.round(vw * s);
  const h = Math.round(vh * s);
  return { x: Math.floor((W - w) / 2), y: top ? 0 : Math.floor((H - h) / 2), w, h };
}

export class Display {
  private gl: WebGL2RenderingContext | null = null;
  private ctx2d: CanvasRenderingContext2D | null = null;
  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private rect: Rect = { x: 0, y: 0, w: 1, h: 1 };
  /** Uniform locations, looked up once. */
  private uniforms: Record<string, WebGLUniformLocation | null> = {};
  private srcW = 224;
  private srcH = 288;

  constructor(readonly canvas: HTMLCanvasElement) {
    try {
      this.gl = canvas.getContext('webgl2', { antialias: false, alpha: false, preserveDrawingBuffer: true });
      if (this.gl) this.setup(this.gl);
    } catch {
      this.gl = null;
    }
    if (!this.gl) {
      this.ctx2d = canvas.getContext('2d');
      if (this.ctx2d) this.ctx2d.imageSmoothingEnabled = false;
    }
  }

  get webgl(): boolean {
    return this.gl !== null;
  }

  private setup(gl: WebGL2RenderingContext): void {
    const compile = (type: number, src: string): WebGLShader => {
      const sh = gl.createShader(type);
      if (!sh) throw new Error('shader');
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) ?? 'compile');
      return sh;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERTEX));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
    this.program = prog;
    for (const name of ['uSrc', 'uDst', 'uScan', 'uMask', 'uBloom', 'uCurve', 'uVignette']) {
      this.uniforms[name] = gl.getUniformLocation(prog, name);
    }
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  /** Size the window canvas to the window in device pixels. */
  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.floor(window.innerWidth * dpr));
    const h = Math.max(1, Math.floor(window.innerHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  present(src: HTMLCanvasElement, crt: CrtMode, scale: ScaleMode, top = false): void {
    this.srcW = src.width;
    this.srcH = src.height;
    const r = fitRect(src.width, src.height, this.canvas.width, this.canvas.height, scale, top);
    this.rect = r;
    const gl = this.gl;
    if (gl && this.program) {
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.viewport(r.x, this.canvas.height - r.y - r.h, r.w, r.h);
      gl.useProgram(this.program);
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
      const p = CRT[crt];
      const u = (name: string): WebGLUniformLocation | null => this.uniforms[name] ?? null;
      gl.uniform2f(u('uSrc'), src.width, src.height);
      gl.uniform2f(u('uDst'), r.w, r.h);
      gl.uniform1f(u('uScan'), r.h / src.height >= 2.5 ? p.scan : p.scan * 0.4);
      gl.uniform1f(u('uMask'), r.w / src.width >= 3 ? p.mask : 0);
      gl.uniform1f(u('uBloom'), p.bloom);
      gl.uniform1f(u('uCurve'), p.curve);
      gl.uniform1f(u('uVignette'), p.vignette);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      return;
    }
    const ctx = this.ctx2d;
    if (!ctx) return;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, r.x, r.y, r.w, r.h);
  }

  /** A pointer position (CSS pixels) as a virtual-screen pixel, or null outside it. */
  toVirtual(clientX: number, clientY: number): { x: number; y: number } | null {
    const dpr = window.devicePixelRatio || 1;
    const x = ((clientX * dpr - this.rect.x) / this.rect.w) * this.srcW;
    const y = ((clientY * dpr - this.rect.y) / this.rect.h) * this.srcH;
    if (x < 0 || y < 0 || x >= this.srcW || y >= this.srcH) return null;
    return { x: Math.floor(x), y: Math.floor(y) };
  }
}
